import { CheckCircle, AlertCircle, CirclePlus } from "lucide-react";
import type { ActorRefFrom } from "xstate";
import { ArtworkList } from "@design-lab/shared/ui/ArtworkList";
import type { transferArtworkMachine } from "@design-lab/entities/transfers-lab/model/transfer-artwork.machine";

export type ArtworksHeaderProps = {
  items: ActorRefFrom<typeof transferArtworkMachine>[];
  selectedId: string | null;
  maxSlots?: number; // default 10
  onUpload: () => void;
  onSelect: (id: string) => void;
};

export function ArtworksHeader(props: ArtworksHeaderProps) {
  const { items, selectedId, maxSlots = 10, onUpload, onSelect } = props;

  // Check if all artworks have quantities set
  const allHaveQty =
    items.length > 0 &&
    items.every(item => {
      const snapshot = item.getSnapshot();
      return snapshot.context.quantity > 0;
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <ArtworkList
          items={items}
          selectedId={selectedId}
          onSelect={onSelect}
          badgeType="quantity"
        />

        {/* Empty slots with upload buttons */}
        {Array.from({ length: Math.max(0, maxSlots - items.length) }).map((_, i) => (
          <button
            key={`upload-${i}`}
            type="button"
            className="group relative flex h-[68px] w-[68px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-blue-600 bg-white transition-colors hover:bg-blue-50"
            onClick={onUpload}
            data-testid={`artworks-header-upload-${i}`}
            aria-label="Upload artwork"
          >
            <CirclePlus className="size-8 text-blue-600" />
          </button>
        ))}
      </div>

      {allHaveQty ? (
        <div
          className="flex w-fit items-center gap-2 rounded-sm border bg-green-200 p-1 text-xs font-semibold"
          role="status"
          aria-live="polite"
        >
          <CheckCircle className="h-5 w-5 min-w-5" />
          <span>All quantities has been added. Proceed to Add to Cart</span>
        </div>
      ) : (
        <div
          className="flex w-fit items-center gap-2 rounded-sm border bg-amber-200 p-1 text-xs font-semibold"
          role="status"
          aria-live="polite"
        >
          <AlertCircle className="h-5 w-5 min-w-5" />
          <span>
            Please select quantities below for all the artworks to continue adding to the cart
          </span>
        </div>
      )}
    </div>
  );
}
